import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Auth0Client, createAuth0Client, User as Auth0User } from '@auth0/auth0-spa-js';
import { Observable, firstValueFrom, from, throwError } from 'rxjs';
import { catchError, concatMap, shareReplay, tap } from 'rxjs/operators';
import { AuthConfig, IobaseAuthConfig } from './auth0-config';

export type Audience = 'Terega' | 'Iobase';

/** Where users land after login when no explicit deep-link target was requested. */
const DEFAULT_POST_LOGIN_ROUTE = '/scenario-list';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);

  // --- Reactive state (signals) ---
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _isAuthenticated = signal(false);
  private readonly _userProfile = signal<Auth0User | null>(null);

  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /**
   * Current authentication state. A signal, so any consumer sees the latest value
   * and is notified when it changes — e.g. once the Auth0 redirect callback has
   * been processed. Writing it schedules change detection regardless of which JS
   * context (worker, iframe, promise) the write happened in — no `NgZone` needed.
   */
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly userProfile = this._userProfile.asReadonly();

  /** RxJS views for the few callers that still want a stream. */
  readonly isAuthenticated$ = toObservable(this._isAuthenticated);
  readonly userProfile$ = toObservable(this._userProfile);

  /** @deprecated use the `isAuthenticated` signal. */
  get loggedIn(): boolean {
    return this._isAuthenticated();
  }

  // --- Auth0 clients (RxJS observables, shared) ---
  private auth0Client$!: Observable<Auth0Client>;
  private iobaseAuth0Client$!: Observable<Auth0Client>;

  /**
   * Resolves once the initial auth bootstrap has finished, including handling the
   * Auth0 redirect callback when the app loads on `/?code=...&state=...`.
   * Guards MUST await this before starting a fresh login, otherwise they can
   * redirect to Auth0 mid-callback and drop the authorization code.
   */
  readonly ready: Promise<void>;

  constructor() {
    this.initAuth0Clients();
    this.ready = this.bootstrapAuthState();
  }

  // --- Initialize both Auth0 clients ---
  private initAuth0Clients(): void {
    this.auth0Client$ = (
      from(
        createAuth0Client({
          domain: AuthConfig.domain,
          clientId: AuthConfig.clientId,
          // Persist the token cache + a refresh token so a page reload restores
          // the session without a fresh interactive login.
          cacheLocation: 'localstorage',
          useRefreshTokens: true,
          // Request the Cortex API token in the login redirect itself, so the guard's
          // getTokenSilently() hits the cache instead of falling back to a popup.
          authorizationParams: {
            redirect_uri: window.location.origin,
            audience: AuthConfig.audience,
            scope: `openid profile email ${AuthConfig.scopes}`,
          },
        })
      ) as Observable<Auth0Client>
    ).pipe(
      shareReplay(1),
      catchError(err => {
        this._error.set(err.message);
        return throwError(() => err);
      })
    );

    this.iobaseAuth0Client$ = (
      from(
        createAuth0Client({
          domain: IobaseAuthConfig.domain,
          clientId: IobaseAuthConfig.clientId,
          cacheLocation: 'localstorage',
          useRefreshTokens: true,
          authorizationParams: {
            redirect_uri: window.location.origin,
            scope: 'openid profile email',
          },
        })
      ) as Observable<Auth0Client>
    ).pipe(
      shareReplay(1),
      catchError(err => {
        this._error.set(err.message);
        return throwError(() => err);
      })
    );
  }

  /**
   * Runs once on app load. Processes the Auth0 redirect callback (if present),
   * settles `isAuthenticated$`, and routes the user to their target page.
   */
  private async bootstrapAuthState(): Promise<void> {
    const client = await firstValueFrom(this.auth0Client$);
    const search = window.location.search;
    const isRedirectCallback = search.includes('code=') && search.includes('state=');
    let target = DEFAULT_POST_LOGIN_ROUTE;

    if (isRedirectCallback) {
      try {
        const cbRes = await client.handleRedirectCallback();
        target = cbRes?.appState?.target || DEFAULT_POST_LOGIN_ROUTE;
      } catch (err) {
        console.error('Auth0 redirect callback failed:', err);
      }
      // Strip ?code&state so a refresh can't re-trigger the code exchange.
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Not returning from a login redirect: try to restore an existing session
      // silently (from the persisted refresh token, or a live Auth0 SSO cookie)
      // before anything decides the user needs to log in again.
      try {
        await client.checkSession();
      } catch (err) {
        console.warn('Auth0 checkSession() found no active session:', err);
      }
    }

    const isAuthenticated = await this.checkAuth();

    if (isAuthenticated) {
      this.getUser$().subscribe();
      if (isRedirectCallback) {
        console.log('Auth: redirect callback handled, navigating to', target);
        this.router.navigateByUrl(target);
      }
    }
  }

  /**
   * Fresh session check against the Auth0 client. Updates the `isAuthenticated`
   * signal and returns the result. Cheap (no network) in the common case; falls
   * back to `checkSession()` when the local check throws.
   */
  async checkAuth(): Promise<boolean> {
    const client = await firstValueFrom(this.auth0Client$);
    let isAuthenticated = false;

    try {
      isAuthenticated = await client.isAuthenticated();
    } catch (err) {
      console.error('Auth0 isAuthenticated() failed, trying checkSession:', err);
      try {
        await client.checkSession();
        isAuthenticated = await client.isAuthenticated();
      } catch (err2) {
        console.error('Auth0 checkSession() also failed:', err2);
      }
    }

    this._isAuthenticated.set(isAuthenticated);
    if (isAuthenticated) this._loading.set(false);
    return isAuthenticated;
  }

  // --- Public methods ---
  getUser$(): Observable<Auth0User | undefined> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap(user => this._userProfile.set(user ?? null))
    );
  }

  login(redirectPath: string = DEFAULT_POST_LOGIN_ROUTE): void {
    this._loading.set(true);
    this.auth0Client$.subscribe((client: Auth0Client) => {
      client.loginWithRedirect({
        appState: { target: redirectPath },
      });
    });
  }

  logout(): void {
    this.auth0Client$.subscribe((client: Auth0Client) => {
      client.logout({
        clientId: AuthConfig.clientId,
        logoutParams: {
          returnTo: window.location.origin,
        },
      });

      // Clear local storage
      localStorage.removeItem('lastSimulationPosition');
      localStorage.removeItem('simulationDate');
      localStorage.removeItem('displaySettings');
      localStorage.removeItem('user');

      this._isAuthenticated.set(false);
      this._userProfile.set(null);
      this.router.navigate(['/login']);
    });
  }

  getToken$(audience: string, scope: string, targetClient: Audience = 'Terega'): Observable<string> {
    const client$ = targetClient === 'Terega' ? this.auth0Client$ : this.iobaseAuth0Client$;

    return client$.pipe(
      concatMap((client: Auth0Client) => from(client.getTokenSilently({ authorizationParams: { audience, scope } }).then(token => token!))),
      catchError(() => {
        // Fallback to popup if silent token fails (e.g., no active session)
        return client$.pipe(
          concatMap((client: Auth0Client) =>
            from(client.getTokenWithPopup({ authorizationParams: { audience, scope } }).then(token => token!))
          )
        );
      })
    );
  }
}
